import type { StaticDataType } from "@/utils/hooks/static/staticHooks"
import type { ColumnValidationType } from "../validations/columnValidations"

export type ImportProfileType = {
  id: "candidates" | "clients" | "contracts"
  name: Record<string, string>
  description: Record<string, string>
  columns: Record<string, ColumnValidationType >
  /** Groups of columns where a row must fill at least one (e.g. e-mail or national id). */
  atLeastOneOf?: string[][]
}

// Función para obtener ejemplos reales de datos estáticos
export const getRealStaticExamples = (
  staticData: {
    ratePeriod?: { data: StaticDataType[] };
    invoiceFrequency?: { data: StaticDataType[] };
    invoiceTimeMode?: { data: StaticDataType[] };
    invoiceMode?: { data: StaticDataType[] };
    gender?: { data: StaticDataType[] };
    countries?: { data: StaticDataType[] };
  },
  isImportContext: boolean = false // Para filtrar opciones en contexto de importación
): {
  ratePeriodExamples: string[];
  invoiceFrequencyExamples: string[];
  invoiceTimeModeExamples: string[];
  invoiceModeExamples: string[];
  genderExamples: string[];
  countryExamples: string[];
  allRatePeriods: StaticDataType[];
  allInvoiceFrequencies: StaticDataType[];
  allInvoiceTimeModes: StaticDataType[];
  allInvoiceModes: StaticDataType[];
  allGenders: StaticDataType[];
  allCountries: StaticDataType[];
} => {
  // Filtrar géneros si estamos en contexto de importación
  const filteredGenders = isImportContext 
    ? staticData.gender?.data?.filter(item => item.value !== 3) || []
    : staticData.gender?.data || [];

  return {
    // Ejemplos para mostrar (primeros 2 elementos)
    ratePeriodExamples: staticData.ratePeriod?.data?.slice(0, 2).map(item => item.label) || ["por hora", "por día"],
    invoiceFrequencyExamples: staticData.invoiceFrequency?.data?.slice(0, 2).map(item => item.label) || ["semanal", "mensual"],
    invoiceTimeModeExamples: staticData.invoiceTimeMode?.data?.slice(0, 2).map(item => item.label) || ["vencido", "adelantado"],
    invoiceModeExamples: staticData.invoiceMode?.data?.slice(0, 2).map(item => item.label) || ["manual", "automático"],
    genderExamples: filteredGenders.slice(0, 2).map(item => item.label) || ["Masculino", "Femenino"],
    countryExamples: staticData.countries?.data?.slice(0, 2).map(item => item.label) || ["Costa Rica", "México"],
    
    // Todos los datos disponibles para validación
    allRatePeriods: staticData.ratePeriod?.data || [],
    allInvoiceFrequencies: staticData.invoiceFrequency?.data || [],
    allInvoiceTimeModes: staticData.invoiceTimeMode?.data || [],
    allInvoiceModes: staticData.invoiceMode?.data || [],
    allGenders: filteredGenders,
    allCountries: staticData.countries?.data || []
  };
};


const scheduleDays: Array<{ key: string; en: string; es: string; sample: [string, string] }> = [
  { key: "schedule_monday", en: "Monday", es: "Lunes", sample: ["08:00-16:00", "08:00-20:00; 20:00-08:00"] },
  { key: "schedule_tuesday", en: "Tuesday", es: "Martes", sample: ["08:00-16:00", "08:00-20:00; 20:00-08:00"] },
  { key: "schedule_wednesday", en: "Wednesday", es: "Miércoles", sample: ["08:00-16:00", "08:00-20:00; 20:00-08:00"] },
  { key: "schedule_thursday", en: "Thursday", es: "Jueves", sample: ["08:00-16:00", "08:00-20:00; 20:00-08:00"] },
  { key: "schedule_friday", en: "Friday", es: "Viernes", sample: ["08:00-16:00", "08:00-20:00; 20:00-08:00"] },
  { key: "schedule_saturday", en: "Saturday", es: "Sábado", sample: ["", "08:00-08:00"] },
  { key: "schedule_sunday", en: "Sunday", es: "Domingo", sample: ["", "08:00-08:00"] },
];

// One optional column per day of the week with the time slots of that day.
const scheduleDayColumns: Record<string, ColumnValidationType> = Object.fromEntries(
  scheduleDays.map((day) => [
    day.key,
    {
      required: false,
      type: "schedule",
      fieldHeader: { en: day.en, es: day.es },
      customError: {
        en: "HH:MM-HH:MM, several separated by ';'. Ending earlier = next day; the same time = 24 h. Empty = no service",
        es: "HH:MM-HH:MM, varios separados por ';'. Si termina antes, es al día siguiente; la misma hora = 24 h. Vacío = sin servicio",
      },
      sampleData: { en: day.sample, es: day.sample },
    } satisfies ColumnValidationType,
  ]),
);

// Función para crear el perfil de contratos con ejemplos dinámicos
export const createContractsProfile = (
  staticData: {
    ratePeriod?: { data: StaticDataType[] };
    invoiceFrequency?: { data: StaticDataType[] };
    invoiceTimeMode?: { data: StaticDataType[] };
    invoiceMode?: { data: StaticDataType[] };
    gender?: { data: StaticDataType[] };
    countries?: { data: StaticDataType[] };
  }
): ImportProfileType => {
  const examples = getRealStaticExamples(staticData, true); // true = isImportContext
  
  return {
  id: "contracts",
  name: {
    en: "Contracts",
    es: "Contratos"
  },
  description: {
    en: "Import contracts with client information and dates",
    es: "Importa contratos con información del cliente y fechas"
  },
    columns: {
    contract_start_date: {
      required: true,
      type: "date",
      fieldHeader: {
        en: "Contract Start Date",
        es: "Fecha de Inicio del Contrato"
      },
      sampleData: {
          en: ["30/4/2024", "4/11/2024"],
          es: ["30/4/2024", "4/11/2024"]
      }
    },
    contract_rate: {
      required: true,
      type: "number",
      fieldHeader: {
        en: "Contract Rate",
        es: "Tarifa del Contrato"
      },
      sampleData: {
          en: [15, 20],
          es: [15, 20]
      }
    },
    contract_rate_period: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Rate Period",
        es: "Periodo de Tarifa"
      },
      sampleData: {
          en: examples.ratePeriodExamples,
          es: examples.ratePeriodExamples
      }
    },
    contract_invoice_frequency: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Invoice Frequency",
        es: "Frecuencia de Facturación"
      },
      sampleData: {
          en: examples.invoiceFrequencyExamples,
          es: examples.invoiceFrequencyExamples
      }
    },
    contract_invoice_time_mode: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Invoice Time Mode",
        es: "Modo de Tiempo de Facturación"
      },
      sampleData: {
          en: examples.invoiceTimeModeExamples,
          es: examples.invoiceTimeModeExamples
      }
    },
    contract_invoice_mode: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Invoice Mode",
        es: "Modo de Facturación"
      },
      sampleData: {
          en: examples.invoiceModeExamples,
          es: examples.invoiceModeExamples
      }
    },
    // The weekly schedule of the contract, one column per day (the demand of its
    // template; employees are assigned in the app).
    ...scheduleDayColumns,
    client_first_name: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Client First Name",
        es: "Nombre del Cliente"
      },
      sampleData: {
          en: ["John", "Jane"],
          es: ["John", "Jane"]
      }
    },
    client_last_name1: {
      required: true,
      type: "string",
      fieldHeader: {
          en: "Client Last Name 1",
          es: "Apellido 1 del Cliente"
      },
      sampleData: {
          en: ["Smith", "Doe"],
          es: ["Smith", "Doe"]
        }
      },
      client_last_name2: {
        required: false,
        type: "string",
        fieldHeader: {
          en: "Client Last Name 2",
          es: "Apellido 2 del Cliente"
        },
        sampleData: {
          en: ["Johnson", "Brown"],
          es: ["Johnson", "Brown"]
      }
    },
    client_email: {
      required: false,
      type: "email",
      fieldHeader: {
        en: "Client Email",
        es: "Correo del Cliente"
      },
      sampleData: {
          en: ["john@example.com", "jane@example.com"],
          es: ["john@example.com", "jane@example.com"]
      }
    },
      client_mobile_1: {
      required: false,
      type: "phone",
      fieldHeader: {
          en: "Client Mobile 1",
          es: "Móvil 1 del Cliente"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      client_mobile_2: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Client Mobile 2",
          es: "Móvil 2 del Cliente"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      client_phone_1: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Client Phone 1",
          es: "Teléfono 1 del Cliente"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      client_phone_2: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Client Phone 2",
          es: "Teléfono 2 del Cliente"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      client_whatsapp: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Client WhatsApp",
          es: "WhatsApp del Cliente"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
      }
    },
    client_address: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client Address",
        es: "Dirección del Cliente"
      },
      sampleData: {
          en: ["123 Main St", "456 Oak Ave"],
          es: ["123 Main St", "456 Oak Ave"]
      }
    },
      client_gender_id: {
      required: true,
      type: "gender",
      fieldHeader: {
        en: "Client Gender",
        es: "Género del Cliente"
      },
      sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
      }
    },
    client_national_id: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client National ID",
          es: "Cédula del Cliente"
      },
      sampleData: {
          en: ["12345678", "87654321"],
          es: ["12345678", "87654321"]
      }
    },
    client_country: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client Country",
        es: "País del Cliente"
      },
      sampleData: {
          en: ["Costa Rica", "México"],
          es: ["Costa Rica", "México"]
      }
    },
    client_state: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client State",
        es: "Estado del Cliente"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
    client_city: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client City",
        es: "Ciudad del Cliente"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
      client_pobox: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Client P.O. Box",
        es: "Apartado Postal del Cliente"
      },
      sampleData: {
          en: ["90210", "64000"],
          es: ["90210", "64000"]
      }
    },
    patient1_first_name: {
      required: true,
      type: "string",
      fieldHeader: {
        en: "Patient 1 First Name",
        es: "Nombre del Paciente 1"
      },
      sampleData: {
          en: ["Maria", "Carlos"],
          es: ["Maria", "Carlos"]
      }
    },
    patient1_last_name1: {
      required: true,
      type: "string",
      fieldHeader: {
          en: "Patient 1 Last Name 1",
          es: "Apellido 1 del Paciente 1"
      },
      sampleData: {
          en: ["Garcia", "Lopez"],
          es: ["Garcia", "Lopez"]
      }
    },
    patient1_last_name2: {
      required: false,
      type: "string",
      fieldHeader: {
          en: "Patient 1 Last Name 2",
          es: "Apellido 2 del Paciente 1"
      },
      sampleData: {
          en: ["Rodriguez", "Martinez"],
          es: ["Rodriguez", "Martinez"]
        }
      },
      patient1_email: {
        required: false,
        type: "email",
      fieldHeader: {
          en: "Patient 1 Email",
          es: "Correo del Paciente 1"
      },
      sampleData: {
          en: ["maria@example.com", "carlos@example.com"],
          es: ["maria@example.com", "carlos@example.com"]
      }
    },
      patient1_mobile_1: {
      required: false,
        type: "phone",
      fieldHeader: {
          en: "Patient 1 Mobile 1",
          es: "Móvil 1 del Paciente 1"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient1_mobile_2: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Patient 1 Mobile 2",
          es: "Móvil 2 del Paciente 1"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient1_phone_1: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Patient 1 Phone 1",
          es: "Teléfono 1 del Paciente 1"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient1_phone_2: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Patient 1 Phone 2",
          es: "Teléfono 2 del Paciente 1"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient1_whatsapp: {
        required: false,
        type: "phone",
        fieldHeader: {
          en: "Patient 1 WhatsApp",
          es: "WhatsApp del Paciente 1"
        },
        sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient1_address: {
        required: false,
        type: "string",
        fieldHeader: {
          en: "Patient 1 Address",
          es: "Dirección del Paciente 1"
        },
        sampleData: {
          en: ["123 Main St", "456 Oak Ave"],
          es: ["123 Main St", "456 Oak Ave"]
        }
      },
      patient1_gender_id: {
        required: true,
        type: "gender",
        fieldHeader: {
          en: "Patient 1 Gender",
          es: "Género del Paciente 1"
        },
        sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
      }
    },
    patient1_national_id: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 1 National ID",
          es: "Cédula del Paciente 1"
      },
      sampleData: {
          en: ["12345678", "87654321"],
          es: ["12345678", "87654321"]
      }
    },
    patient1_country: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 1 Country",
        es: "País del Paciente 1"
      },
      sampleData: {
          en: ["Costa Rica", "México"],
          es: ["Costa Rica", "México"]
      }
    },
    patient1_state: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 1 State",
        es: "Estado del Paciente 1"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
    patient1_city: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 1 City",
        es: "Ciudad del Paciente 1"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
      patient1_pobox: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 1 P.O. Box",
        es: "Apartado Postal del Paciente 1"
      },
      sampleData: {
          en: ["90210", "64000"],
          es: ["90210", "64000"]
      }
    },
      patient1_birth_date: {
      required: false,
        type: "date",
      fieldHeader: {
          en: "Patient 1 Birth Date",
          es: "Fecha de Nacimiento del Paciente 1"
      },
      sampleData: {
          en: ["15/3/1990", "22/8/1985"],
          es: ["15/3/1990", "22/8/1985"]
      }
    },
      patient1_gender_id_preferred: {
      required: false,
        type: "gender",
      fieldHeader: {
          en: "Patient 1 Preferred Gender",
          es: "Género Preferido del Paciente 1"
      },
      sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
      }
    },
      patient2_first_name: {
      required: false,
        conditionalRequired: true,
      type: "string",
      fieldHeader: {
          en: "Patient 2 First Name",
          es: "Nombre del Paciente 2"
      },
       sampleData: {
          en: ["Ana", "Pedro"],
          es: ["Ana", "Pedro"]
      }
    },
      patient2_last_name1: {
      required: false,
        conditionalRequired: true,
      type: "string",
      fieldHeader: {
          en: "Patient 2 Last Name 1",
          es: "Apellido 1 del Paciente 2"
      },
      sampleData: {
          en: ["Hernandez", "Silva"],
          es: ["Hernandez", "Silva"]
      }
    },
      patient2_last_name2: {
      required: false,
        conditionalRequired: true,
      type: "string",
      fieldHeader: {
          en: "Patient 2 Last Name 2",
          es: "Apellido 2 del Paciente 2"
      },
      sampleData: {
          en: ["Morales", "Vega"],
          es: ["Morales", "Vega"]
        }
      },
      patient2_email: {
        required: false,
        type: "email",
        fieldHeader: {
          en: "Patient 2 Email",
          es: "Correo del Paciente 2"
        },
        sampleData: {
          en: ["ana@example.com", "pedro@example.com"],
          es: ["ana@example.com", "pedro@example.com"]
        }
      },
      patient2_mobile_1: {
      required: false,
      type: "phone",
      fieldHeader: {
          en: "Patient 2 Mobile 1",
          es: "Móvil 1 del Paciente 2"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
      }
    },
      patient2_mobile_2: {
      required: false,
        type: "phone",
      fieldHeader: {
          en: "Patient 2 Mobile 2",
          es: "Móvil 2 del Paciente 2"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient2_phone_1: {
        required: false,
        type: "phone",
      fieldHeader: {
          en: "Patient 2 Phone 1",
          es: "Teléfono 1 del Paciente 2"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient2_phone_2: {
        required: false,
        type: "phone",
      fieldHeader: {
          en: "Patient 2 Phone 2",
          es: "Teléfono 2 del Paciente 2"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
      }
    },
      patient2_whatsapp: {
      required: false,
        type: "phone",
      fieldHeader: {
          en: "Patient 2 WhatsApp",
          es: "WhatsApp del Paciente 2"
      },
      sampleData: {
          en: ["+1234567890", "+0987654321"],
          es: ["+1234567890", "+0987654321"]
        }
      },
      patient2_address: {
        required: false,
        type: "string",
      fieldHeader: {
          en: "Patient 2 Address",
          es: "Dirección del Paciente 2"
      },
      sampleData: {
          en: ["123 Main St", "456 Oak Ave"],
          es: ["123 Main St", "456 Oak Ave"]
      }
    },
      patient2_gender_id: {
      required: false,
        type: "gender",
      fieldHeader: {
          en: "Patient 2 Gender",
          es: "Género del Paciente 2"
      },
      sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
      }
    },
    patient2_national_id: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 2 National ID",
          es: "Cédula del Paciente 2"
      },
      sampleData: {
          en: ["12345678", "87654321"],
          es: ["12345678", "87654321"]
      }
    },
    patient2_country: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 2 Country",
        es: "País del Paciente 2"
      },
      sampleData: {
          en: ["Costa Rica", "México"],
          es: ["Costa Rica", "México"]
      }
    },
    patient2_state: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 2 State",
        es: "Estado del Paciente 2"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
    patient2_city: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 2 City",
        es: "Ciudad del Paciente 2"
      },
      sampleData: {
          en: ["San José", "Guadalajara"],
          es: ["San José", "Guadalajara"]
      }
    },
      patient2_pobox: {
      required: false,
      type: "string",
      fieldHeader: {
        en: "Patient 2 P.O. Box",
        es: "Apartado Postal del Paciente 2"
      },
      sampleData: {
          en: ["90210", "64000"],
          es: ["90210", "64000"]
      }
    },
      patient2_birth_date: {
      required: false,
        type: "date",
      fieldHeader: {
          en: "Patient 2 Birth Date",
          es: "Fecha de Nacimiento del Paciente 2"
      },
      sampleData: {
          en: ["10/6/1992", "18/12/1988"],
          es: ["10/6/1992", "18/12/1988"]
      }
    },
      patient2_gender_id_preferred: {
      required: false,
        type: "gender",
      fieldHeader: {
          en: "Patient 2 Preferred Gender",
          es: "Género Preferido del Paciente 2"
      },
      sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
        }
      }
    }
  };
};

// Función para crear el perfil de candidatos con ejemplos dinámicos
export const createCandidatesProfile = (
  staticData: {
    ratePeriod?: { data: StaticDataType[] };
    invoiceFrequency?: { data: StaticDataType[] };
    invoiceTimeMode?: { data: StaticDataType[] };
    invoiceMode?: { data: StaticDataType[] };
    gender?: { data: StaticDataType[] };
    countries?: { data: StaticDataType[] };
  }
): ImportProfileType => {
  const examples = getRealStaticExamples(staticData, true); // true = isImportContext
  
  return {
    id: "candidates",
    name: {
      en: "Candidates",
      es: "Candidatos"
    },
    description: {
      en: "Import candidates with personal and professional information",
      es: "Importar candidatos con informacion personal y profesional"
    },
    // The person is identified by the e-mail, or inside the company by the national id.
    atLeastOneOf: [["email", "national_id"]],
    columns: {
      first_name: {
        required: true,
      type: "string",
        fieldHeader:{
          en: "Name",
          es: "Nombre"
      },
      sampleData: {
          en: ["Juan", "Maria"],
          es: ["Juan", "Maria"]
      }
    },
      last_name1: {
        required: true,
      type: "string",
        fieldHeader: { en: "First Last Name", es: "Primer Apellido" },
      sampleData: {
          en: ["Perez", "Gonzalez"],
          es: ["Perez", "Gonzalez"]
      }
    },
      last_name2: {
        required: true,
      type: "string",
        fieldHeader: { en: "Second Last Name", es: "Segundo Apellido" },
      sampleData: {
          en: ["Ramirez", "Lopez"],
          es: ["Ramirez", "Lopez"]
        }
      },
      country: {
        required: true,
        type: "country",
        fieldHeader: { en: "Country", es: "Pais" },
      sampleData: {
          en: examples.countryExamples,
          es: examples.countryExamples
      }
    },
      gender: {
      required: false,
        type: "gender",
        fieldHeader: { en: "Gender", es: "Genero" },
        sampleData: {
          en: examples.genderExamples,
          es: examples.genderExamples
        }
      },
      birth_date: {
        required: false,
        type: "date",
        fieldHeader: { en: "Date of Birth", es: "Fecha de Nacimiento" },
       sampleData: {
          en: ["30/4/1975", "4/11/1980"],
          es: ["30/4/1975", "4/11/1980"]
      }
    },
      national_id: {
      required: false,
      type: "string",
        fieldHeader: { en: "National ID", es: "# Identificacion" },
        sampleData: {
          en: ["12345678", "87654321"],
          es: ["12345678", "87654321"]
        }
      },
      email: {
        required: false,
        type: "email",
        fieldHeader: { en: "Email", es: "Correo Electronico" },
      sampleData: {
          en: ["juan@example.com", "maria@example.com"],
          es: ["juan@example.com", "maria@example.com"]
      }
    },
      mobile1: {
      required: false,
      type: "phone",
        fieldHeader: { en: "Mobile 1", es: "Movil 1" },
        sampleData: {
          en: ["+1 600123456", "+1 611987654"],
          es: ["+1 600123456", "+1 611987654"]
        }
      },
      mobile2: {
        required: false,
        type: "phone",
        fieldHeader: { en: "Mobile 2", es: "Movil 2" },
       sampleData: {
          en: ["+1 9999999999", "+1 9999999999"],
          es: ["+1 9999999999", "+1 9999999999"]
      }
    },
      whatsapp: {
      required: false,
        type: "phone",
        fieldHeader: { en: "WhatsApp", es: "WhatsApp" },
        sampleData: {
          en: ["+1 9999999999", "+1 9999999999"],
          es: ["+1 9999999999", "+1 9999999999"]
        }
      }
    }
  };
};

// Función para obtener los perfiles disponibles
export const getAvailableProfiles = (
  staticData?: {
    ratePeriod?: { data: StaticDataType[] };
    invoiceFrequency?: { data: StaticDataType[] };
    invoiceTimeMode?: { data: StaticDataType[] };
    invoiceMode?: { data: StaticDataType[] };
    gender?: { data: StaticDataType[] };
    countries?: { data: StaticDataType[] };
  }
): ImportProfileType[] => {
  const profiles: ImportProfileType[] = [];

  // Siempre usar createCandidatesProfile y createContractsProfile
  if (staticData) {
    const dynamicCandidatesProfile = createCandidatesProfile(staticData);
    const dynamicContractsProfile = createContractsProfile(staticData);
    profiles.splice(0, 0, dynamicCandidatesProfile);
    profiles.splice(1, 0, dynamicContractsProfile);
  } else {
    // Si no hay datos estáticos, crear perfiles con datos vacíos
    const emptyStaticData = {
      ratePeriod: { data: [] },
      invoiceFrequency: { data: [] },
      invoiceTimeMode: { data: [] },
      invoiceMode: { data: [] },
      gender: { data: [] },
      countries: { data: [] }
    };
    const fallbackCandidatesProfile = createCandidatesProfile(emptyStaticData);
    const fallbackContractsProfile = createContractsProfile(emptyStaticData);
    profiles.splice(0, 0, fallbackCandidatesProfile);
    profiles.splice(1, 0, fallbackContractsProfile);
  }

  return profiles;
};

// Array estático para compatibilidad (usar getAvailableProfiles en su lugar)
export const availableProfiles: ImportProfileType[] = [
  createCandidatesProfile({
    ratePeriod: { data: [] },
    invoiceFrequency: { data: [] },
    invoiceTimeMode: { data: [] },
    invoiceMode: { data: [] },
    gender: { data: [] },
    countries: { data: [] }
  }),
  createContractsProfile({
    ratePeriod: { data: [] },
    invoiceFrequency: { data: [] },
    invoiceTimeMode: { data: [] },
    invoiceMode: { data: [] },
    gender: { data: [] },
    countries: { data: [] }
  })
]